import { convertKmsToMiles } from 'utils/helpers/unitSystem';
import { type AssetCostData } from '../../DashboardV2/Components/KPIsGraphs';
import {
  type DownTimeDataItem,
  type PartsRecommendationItem,
} from 'store/SquaresStore/PartsRecommendationsState.slice';
import {
  type AssetDetail,
  type ServiceView,
} from 'types/survivalAnalysis.types';
import { type AssetDetails } from 'types/squares.type';

interface CategoryStat {
  category: string;
  count: number;
  cost: number;
  total_part_cost: number;
  total_labour_cost: number;
}

interface WorkOrderStats {
  pmCost: number;
  cmCost: number;
  pmCount: number;
  cmCount: number;
  openCount: number;
  closedCount: number;
  backlogCount: number;
  repairCount: number;
  replaceCount: number;
  otherActionCount: number;
  topAssetByWorkOrders: {
    bumper_number: string;
    count: number;
  } | null;
}

interface DowntimeStats {
  totalPmDuration: number;
  totalCmDuration: number;
  topAssetByDowntime: {
    bumper_number: string;
    pmDuration: number;
    cmDuration: number;
    totalDuration: number;
  } | null;
}

export const formatNumberToShortScale = (
  value: number | string,
  decimalPlaces = 2
) => {
  const num = Number(value);

  if (isNaN(num)) {
    return 'Invalid input';
  }

  if (num >= 1_000_000_000) {
    return (num / 1_000_000_000).toFixed(decimalPlaces) + 'b';
  } else if (num >= 1_000_000) {
    return (num / 1_000_000).toFixed(decimalPlaces) + 'm';
  } else if (num >= 1_000) {
    return (num / 1_000).toFixed(decimalPlaces) + 'k';
  } else {
    return num.toFixed(decimalPlaces);
  }
};

export const getSortedCategoryWiseWorkOrderData = (
  data: any[],
  options?: { sortByCount?: boolean; onlyReplace?: boolean }
): CategoryStat[] => {
  const { sortByCount = false, onlyReplace = false } = options ?? {};
  const acc: Record<string, CategoryStat & { workOrders: Set<string> }> = {};

  data.forEach((item) => {
    if (onlyReplace && item.actions_performed !== 'REPLACE') return;

    // Category key depends on "onlyReplace"
    const subCategory =
      item.sub_component_categories?.replace(/^\[|\]$/g, '') || '';
    const categoryKey = onlyReplace
      ? `${item.components_category} - ${subCategory}`
      : item.components_category;

    const totalCost = Number(item.total_cost) || 0;
    const totalPartCost = Number(item?.comp_part_cost) || 0;
    const totalLabourCost = Number(item?.comp_labour_cost) || 0;
    const workOrderId = String(
      item.work_order_id ?? item.work_order_number ?? ''
    );

    if (!acc[categoryKey]) {
      acc[categoryKey] = {
        category: categoryKey,
        count: 0,
        cost: 0,
        total_part_cost: 0,
        total_labour_cost: 0,
        workOrders: new Set(),
      };
    }

    acc[categoryKey].cost += totalCost;
    acc[categoryKey].total_part_cost += totalPartCost;
    acc[categoryKey].total_labour_cost += totalLabourCost;

    if (workOrderId) {
      acc[categoryKey].workOrders.add(workOrderId);
    }
  });

  const result: CategoryStat[] = Object.values(acc).map((stat) => ({
    category: stat.category,
    count: stat.workOrders.size,
    cost: stat.cost,
    total_part_cost: stat.total_part_cost,
    total_labour_cost: stat.total_labour_cost,
  }));

  return result.sort((a, b) =>
    sortByCount ? b.count - a.count : b.cost - a.cost
  );
};

export const maintenanceCostDistributionDataFormatter = (
  orders: any[],
  targetValue: number,
  today: Date = new Date()
) => {
  const monthlyMap = new Map<string, { cost: number; date: Date }>();

  let totalCost = 0;

  // Group costs by month
  for (const order of orders) {
    const date = new Date(order.date_of_orders);
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
      2,
      '0'
    )}`;

    const cost = Number(order.total_cost) || 0;
    totalCost += cost;

    const existing = monthlyMap.get(key);
    if (existing) {
      existing.cost += cost;
    } else {
      monthlyMap.set(key, {
        cost,
        date: new Date(date.getFullYear(), date.getMonth(), 1),
      });
    }
  }

  // Sort by month
  const sortedEntries = [...monthlyMap.entries()].sort(
    (a, b) => a[1].date.getTime() - b[1].date.getTime()
  );

  //  chartData + trend + max
  let runningSum = 0;
  let maxValue = -Infinity;

  const chartData = sortedEntries.map(([_, { cost, date }], idx) => {
    runningSum += cost;
    const trend = runningSum / (idx + 1);
    maxValue = Math.max(maxValue, cost, trend);

    const name = date.toLocaleString('default', {
      month: 'long',
      year: 'numeric',
    });

    return { name, cost: +cost.toFixed(2), trend };
  });

  // Derived values
  const currentMonth = today.getMonth() + 1; // Jan = 1
  const targetedAverageMonthlyMaintenanceCost = targetValue / 12;
  const averageMonthlyMaintenanceCost =
    currentMonth > 0 ? totalCost / currentMonth : 0;

  const chartMaxValue =
    Math.max(
      targetedAverageMonthlyMaintenanceCost,
      averageMonthlyMaintenanceCost,
      maxValue ?? 0
    ) + 5000;

  return {
    chartData,
    chartMaxValue,
    targetedAverageMonthlyMaintenanceCost,
    totalCost,
  };
};

export const aggregateAssetWiseData = (data: any) => {
  const temp: AssetCostData[] = Object.values(data || {});

  if (temp.length === 0) {
    return {
      total_cost: 0,
      total_labour_cost: 0,
      total_part_cost: 0,
      totalTonnage: 0,
      totalEngineHours: 0,
      totalMiles: 0,
    };
  }
  const aggregated = temp.reduce(
    (acc, asset) => {
      const totalCost = Number(asset.total_cost);
      const labourCost = Number(asset.total_labour_cost);
      const partCost = Number(asset.total_part_cost);
      acc.total_cost += !isNaN(totalCost) ? totalCost : 0;
      acc.total_labour_cost += !isNaN(labourCost) ? labourCost : 0;
      acc.total_part_cost += !isNaN(partCost) ? partCost : 0;

      // only add ton, distance and engine hr if the cost is greater than 0
      const validCost = !isNaN(totalCost) && totalCost > 0;
      if (validCost) {
        const tonnage = Number(asset.total_tonnage) || 0;
        const durationMins = Number(asset.total_engine_duration_mins) || 0;
        const kms = Number(asset.total_distance_kms) || 0;

        acc.totalTonnage += isNaN(tonnage) ? 0 : tonnage;
        acc.totalEngineHours += isNaN(durationMins) ? 0 : durationMins / 60;
        acc.totalMiles += isNaN(kms) ? 0 : convertKmsToMiles(kms);
      }

      return acc;
    },
    {
      total_cost: 0,
      total_labour_cost: 0,
      total_part_cost: 0,
      totalTonnage: 0,
      totalEngineHours: 0,
      totalMiles: 0,
    }
  );
  return aggregated;
};

export const calculateWorkOrderStats = (
  workOrders: PartsRecommendationItem[]
): WorkOrderStats => {
  // map asset_id → count
  const assetWorkOrderCount: Record<string, number> = {};

  const stats = workOrders.reduce<WorkOrderStats>(
    (acc, order) => {
      const category = order.category?.toLowerCase?.() || '';
      const status = order.work_order_status?.toLowerCase?.() || '';
      const action = order.actions_performed?.toLowerCase?.() || '';
      const cost = Number(order.total_cost) || 0;

      // PM vs CM
      if (category === 'pm') {
        acc.pmCost += cost;
        acc.pmCount += 1;
      } else if (category === 'cm') {
        acc.cmCost += cost;
        acc.cmCount += 1;
      }

      // Open vs Closed
      if (status === 'open') {
        acc.openCount += 1;
      } else if (status === 'closed') {
        acc.closedCount += 1;
      } else if (status === 'backlog') {
        acc.backlogCount += 1;
      }

      // Actions performed
      if (action.includes('repair')) {
        acc.repairCount += 1;
      } else if (action.includes('replace')) {
        acc.replaceCount += 1;
      } else if (action) {
        acc.otherActionCount += 1;
      }

      // Asset count
      if (order.asset_id) {
        assetWorkOrderCount[order.asset_id] =
          (assetWorkOrderCount[order.asset_id] || 0) + 1;
      }

      return acc;
    },
    {
      pmCost: 0,
      cmCost: 0,
      pmCount: 0,
      cmCount: 0,
      openCount: 0,
      closedCount: 0,
      backlogCount: 0,
      repairCount: 0,
      replaceCount: 0,
      otherActionCount: 0,
      topAssetByWorkOrders: null,
    }
  );

  // find max
  let maxAsset: string | null = null;
  let maxCount = 0;
  for (const [assetId, count] of Object.entries(assetWorkOrderCount)) {
    if (count > maxCount) {
      maxCount = count;
      maxAsset = assetId;
    }
  }

  stats.topAssetByWorkOrders =
    maxAsset !== null ? { bumper_number: maxAsset, count: maxCount } : null;

  return stats;
};

export const calculateDowntimeStats = (
  downtimeData: DownTimeDataItem[]
): DowntimeStats => {
  return downtimeData.reduce<DowntimeStats>(
    (acc, item) => {
      const pmDurationInHours = (item.pm_duration || 0) / 3600;
      const cmDurationInHours = (item.cm_duration || 0) / 3600;

      acc.totalPmDuration += pmDurationInHours;
      acc.totalCmDuration += cmDurationInHours;

      const total = pmDurationInHours + cmDurationInHours;

      if (
        !acc.topAssetByDowntime ||
        total > acc.topAssetByDowntime.totalDuration
      ) {
        acc.topAssetByDowntime = {
          bumper_number: item.asset_id,
          pmDuration: pmDurationInHours,
          cmDuration: cmDurationInHours,
          totalDuration: total,
        };
      }

      return acc;
    },
    {
      totalPmDuration: 0,
      totalCmDuration: 0,
      topAssetByDowntime: null,
    }
  );
};

export const calculatePmVsCmCostFromWorkOrders = (
  workOrders: PartsRecommendationItem[]
): {
  totalPmCost: number;
  totalCmCost: number;
} => {
  return workOrders.reduce<{
    totalPmCost: number;
    totalCmCost: number;
  }>(
    (acc, order) => {
      if (order.category === 'PM') {
        acc.totalPmCost += order.total_cost || 0;
      } else if (order.category === 'CM') {
        acc.totalCmCost += order.total_cost || 0;
      }
      return acc;
    },
    { totalPmCost: 0, totalCmCost: 0 }
  );
};

// here in old dashboard we were mixing survival analysis and healthscore data which makes the
//  service view to be set when user want to view specific asset
// so in order to not interfere that logic and also not to take survivaal analysis
// data which is anywatys not needed< i am adding dummy vals to amke service view
// not these dummy values are not used in src/views/Settings/ExternalUsers/Squares/DashboardV2/Components/AssetDetailsView.tsx
export const convertAssetHealthScoreDataToServiceView = (
  asset: AssetDetails
): ServiceView => {
  const tempServiceView: ServiceView = {
    // Required ServiceView fields with dummy placeholders
    work_order_age_category: '',
    work_order_status: '',
    description: '',
    components_category: '',
    sub_component: '',
    componentsCategories: new Set<string>(),
    subComponentCategories: new Set<string>(),
    componentsCategoryAssets: new Set<AssetDetail>(),

    // value that exists and are used in AssetDetailsView
    item_id: asset.bumper_number, // fallback: use bumper number as ID
    model: asset.model,
    oem: asset.make,
    type: asset.asset_type,
    lastConnected: asset.lastConnected,
    date_of_orders: asset.latest_work_order_date,
    work_order_age_in_days: Number(asset.age_in_days),
  };
  return tempServiceView;
};
